// Deviations screen, ported from renderDeviationsView/openNewDeviationModal/
// openDeviationModal (source lines 7839-7977).
import { deviationsApi, deviationActionsApi, patientsApi, usersApi, studiesApi } from "./api-client.js";
import { escapeHtml, fmtDateOnly, openModal, closeModal, notify, $, $$ } from "./dom.js";
import { STATE, canWriteSite, canReportToSponsor } from "./state.js";
import { sponsorReportDeadline, SPONSOR_REPORT_REQUIRED_TYPES } from "@woof/shared";

const DEVIATION_TYPES = ["CRITICAL", "MAJOR", "MINOR", "PLANNED"];
const DEVIATION_TYPE_BADGE: Record<string, string> = { CRITICAL: "danger", MAJOR: "warning", MINOR: "info", PLANNED: "success" };

let filterPid = "";
let filterType = "";
let filterStatus = "";

export async function renderDeviationsView(root: HTMLElement): Promise<void> {
  root.innerHTML = `<div class="loading">Chargement…</div>`;
  const [all, patients, studies] = await Promise.all([deviationsApi.list(), patientsApi.list(), studiesApi.list()]);
  const studyName = (id: number | null) => (id == null ? "—" : studies.find((s: any) => s.id === id)?.name ?? "—");
  let list = all;
  if (filterPid) list = list.filter((d: any) => d.patientId === filterPid);
  if (filterType) list = list.filter((d: any) => d.dvcat === filterType);
  if (filterStatus) list = list.filter((d: any) => d.status === filterStatus);

  const patOpts = `<option value="">Tous patients</option>` + patients.map((p: any) => `<option value="${p.id}" ${filterPid === p.id ? "selected" : ""}>${p.id}</option>`).join("");
  const typeOpts = ["", ...DEVIATION_TYPES].map((o) => `<option value="${o}" ${filterType === o ? "selected" : ""}>${o || "Tous types"}</option>`).join("");
  const statusOpts = ["", "Ouverte", "En cours", "Résolue", "Fermée"].map((o) => `<option value="${o}" ${filterStatus === o ? "selected" : ""}>${o || "Tous statuts"}</option>`).join("");

  const rows = list.length
    ? list
        .map(
          (d: any) => `<tr>
        <td>${escapeHtml(d.pdid)}</td><td>${escapeHtml(d.patientId || "—")}</td>
        <td>${escapeHtml(studyName(d.studyId))}</td>
        <td><span class="badge badge-${DEVIATION_TYPE_BADGE[d.dvcat] || "info"}">${d.dvcat}</span></td>
        <td>${escapeHtml(d.dvterm || "")}</td><td>${fmtDateOnly(d.date)}</td>
        <td>${escapeHtml(d.status || "")}${d.sponsorReportOverdue ? ` <span class="badge badge-danger" title="Signalement au sponsor en retard">⚠️ Signalement en retard</span>` : ""}</td>
        <td>${escapeHtml(d.assignedTo || "")}</td>
        <td><button class="btn btn-sm" data-open-dev="${d.id}">Voir</button></td>
      </tr>`
        )
        .join("")
    : `<tr><td colspan="9" class="muted">Aucune déviation.</td></tr>`;

  const canCreate = STATE.user && patients.some((p: any) => canWriteSite(p.siteid));

  root.innerHTML = `
    <div class="view-header"><h2>📌 Déviations protocole</h2><p class="muted">${list.length} / ${all.length} déviations</p></div>
    <div class="filters-bar">
      <select id="devFilterPid">${patOpts}</select>
      <select id="devFilterType">${typeOpts}</select>
      <select id="devFilterStatus">${statusOpts}</select>
      ${canCreate ? `<button class="btn btn-primary" id="btnNewDev">+ Nouvelle déviation</button>` : ""}
    </div>
    <table class="data-table"><thead><tr><th>ID</th><th>Patient</th><th>Étude</th><th>Type</th><th>Titre</th><th>Date</th><th>Statut</th><th>Assigné</th><th></th></tr></thead><tbody>${rows}</tbody></table>`;

  $("#devFilterPid")!.addEventListener("change", (e) => { filterPid = (e.target as HTMLSelectElement).value; void renderDeviationsView(root); });
  $("#devFilterType")!.addEventListener("change", (e) => { filterType = (e.target as HTMLSelectElement).value; void renderDeviationsView(root); });
  $("#devFilterStatus")!.addEventListener("change", (e) => { filterStatus = (e.target as HTMLSelectElement).value; void renderDeviationsView(root); });
  $("#btnNewDev")?.addEventListener("click", () => void openNewDeviationModal(patients, root));
  root.querySelectorAll<HTMLElement>("[data-open-dev]").forEach((btn) => {
    btn.addEventListener("click", () => openDeviationModal(btn.dataset.openDev!, root));
  });
}

async function openNewDeviationModal(patients: any[], root: HTMLElement): Promise<void> {
  const users = await usersApi.directory();
  // Admin is a system-management role, not someone deviations get assigned
  // to for follow-up - excluded from this picker specifically (the
  // directory endpoint itself stays general-purpose for other future uses).
  const assignableUsers = users.filter((u: any) => u.role !== "ADMIN");
  const sponsorUser = assignableUsers.find((u: any) => u.role === "SPONSOR");
  const patOpts = `<option value="">— Sélectionner —</option>` + patients.map((p) => `<option value="${p.id}">${p.id}</option>`).join("");
  const typeOpts = DEVIATION_TYPES.map((o) => `<option value="${o}">${o}</option>`).join("");
  const assignedOpts = assignableUsers
    .map((u: any) => `<option value="${escapeHtml(u.fullName)}" ${sponsorUser && u.id === sponsorUser.id ? "selected" : ""}>${escapeHtml(u.fullName)} (${escapeHtml(u.role)})</option>`)
    .join("");
  openModal(
    "Nouvelle déviation protocole",
    `<div class="form-grid">
      <div class="form-group"><label>Patient <span class="required-mark">*</span></label><select id="dev_pid">${patOpts}</select></div>
      <div class="form-group"><label>Criticité <span class="required-mark">*</span></label><select id="dev_type">${typeOpts}</select></div>
      <div class="form-group"><label>Titre <span class="required-mark">*</span></label><input id="dev_title"></div>
      <div class="form-group"><label>Date <span class="required-mark">*</span></label><input type="date" id="dev_date"></div>
      <div class="form-group" style="grid-column:1/-1"><label>Analyse des causes racines</label><textarea id="dev_desc" rows="3"></textarea></div>
      <div class="form-group"><label>Assigné à</label><select id="dev_assigned">${assignedOpts}</select></div>
    </div>`,
    `<button class="btn btn-secondary" id="devCancel">Annuler</button><button class="btn btn-primary" id="devSave">Créer</button>`
  );
  $("#devCancel")!.addEventListener("click", closeModal);
  $("#devSave")!.addEventListener("click", async () => {
    const patientId = ($("#dev_pid") as HTMLSelectElement).value;
    if (!patientId) return notify("error", "Patient obligatoire");
    const dvterm = ($("#dev_title") as HTMLInputElement).value.trim();
    if (!dvterm) return notify("error", "Titre obligatoire");
    const date = ($("#dev_date") as HTMLInputElement).value;
    if (!date) return notify("error", "Date obligatoire");
    await deviationsApi.create({
      patientId,
      dvcat: ($("#dev_type") as HTMLSelectElement).value,
      dvterm,
      date,
      description: ($("#dev_desc") as HTMLTextAreaElement).value || undefined,
      assignedTo: ($("#dev_assigned") as HTMLSelectElement).value || undefined,
    });
    closeModal();
    void renderDeviationsView(root);
  });
}

const ACTION_KIND_LABEL: Record<string, string> = { corrective: "action corrective", preventive: "action préventive" };

function renderActionRow(deviationId: string, a: any, canEdit: boolean): string {
  const deliverable = a.deliverableFilename
    ? `<a href="${deviationActionsApi.deliverableUrl(deviationId, a.id)}" target="_blank" rel="noopener">📎 ${escapeHtml(a.deliverableFilename)}</a>
       ${canEdit ? `<button class="btn btn-sm btn-danger" data-capa-remove-file="${a.id}" title="Retirer le justificatif">×</button>` : ""}`
    : canEdit
      ? `<input type="file" data-capa-file="${a.id}">`
      : `<span class="muted">—</span>`;
  return `<div class="capa-action-row" data-capa-action="${a.id}" style="display:grid;grid-template-columns:1fr 150px 1fr auto;gap:8px;align-items:start;padding:8px 0;border-bottom:1px solid var(--border-color, #e5e7eb)">
    <textarea rows="2" data-capa-desc="${a.id}" ${canEdit ? "" : "disabled"} placeholder="Description">${escapeHtml(a.description || "")}</textarea>
    <input type="date" data-capa-due="${a.id}" value="${a.dueDate || ""}" ${canEdit ? "" : "disabled"}>
    <div>${deliverable}</div>
    ${canEdit ? `<button class="btn btn-sm btn-danger" data-capa-delete="${a.id}" title="Supprimer">×</button>` : "<span></span>"}
  </div>`;
}

function renderActionList(deviationId: string, kind: "corrective" | "preventive", actions: any[], canEdit: boolean): string {
  const rows = actions.filter((a) => a.kind === kind).map((a) => renderActionRow(deviationId, a, canEdit)).join("");
  return `<div class="capa-action-list" data-capa-list="${kind}">
    ${rows || `<p class="muted" style="margin:4px 0">Aucune ${ACTION_KIND_LABEL[kind]}.</p>`}
    ${canEdit ? `<div data-capa-add-row="${kind}" style="margin-top:6px">${renderAddButton(kind)}</div>` : ""}
  </div>`;
}

function renderAddButton(kind: "corrective" | "preventive"): string {
  return `<button class="btn btn-sm btn-secondary" data-capa-add="${kind}">+ Ajouter ${ACTION_KIND_LABEL[kind]}</button>`;
}

// Inline "new action" form, same grid layout as an existing action row so it
// reads as a natural extension of the list rather than a separate control.
function renderNewActionForm(kind: "corrective" | "preventive"): string {
  return `<div class="capa-action-row" style="display:grid;grid-template-columns:1fr 150px 1fr auto;gap:8px;align-items:start;padding:8px 0;border-bottom:1px solid var(--border-color, #e5e7eb)">
    <textarea rows="2" data-capa-new-desc="${kind}" placeholder="Description de la nouvelle ${ACTION_KIND_LABEL[kind]}" autofocus></textarea>
    <input type="date" data-capa-new-due="${kind}">
    <div class="muted" style="font-size:12px;padding-top:6px">Justificatif ajoutable après création</div>
    <div style="display:flex;flex-direction:column;gap:4px">
      <button class="btn btn-sm btn-primary" data-capa-new-save="${kind}" title="Ajouter">✓</button>
      <button class="btn btn-sm btn-secondary" data-capa-new-cancel="${kind}" title="Annuler">×</button>
    </div>
  </div>`;
}

async function openDeviationModal(id: string, root: HTMLElement): Promise<void> {
  const [d, actions, studies] = await Promise.all([deviationsApi.get(id), deviationActionsApi.list(id), studiesApi.list()]);
  const studyName = d.studyId != null ? studies.find((s: any) => s.id === d.studyId)?.name ?? "—" : "—";
  const writable = canWriteSite(d.siteCode);
  // Sponsor-gated fields (reportedToSponsor/sponsorReportDate/CAPA) are
  // authorized independently of site-write on the backend - SPONSOR is
  // globally read-only for site-write purposes but must still be able to use
  // the fields that exist for it, so this must NOT be ANDed with `writable`.
  const canSponsor = canReportToSponsor();
  const sponsorRequired = SPONSOR_REPORT_REQUIRED_TYPES.includes(d.dvcat);
  const deadline = sponsorRequired ? sponsorReportDeadline(d) : null;
  const optStatus = ["Ouverte", "En cours", "Résolue", "Fermée"].map((o) => `<option value="${o}" ${d.status === o ? "selected" : ""}>${o}</option>`).join("");
  openModal(
    `Déviation ${d.pdid}`,
    `<div class="form-grid">
      <div class="form-group"><label>Patient</label><div>${escapeHtml(d.patientId || "—")}</div></div>
      <div class="form-group"><label>Étude</label><div>${escapeHtml(studyName)}</div></div>
      <div class="form-group"><label>Criticité</label><div><span class="badge badge-${DEVIATION_TYPE_BADGE[d.dvcat] || "info"}">${d.dvcat}</span></div></div>
      <div class="form-group" style="grid-column:1/-1"><label>Titre</label><div>${escapeHtml(d.dvterm || "")}</div></div>
      <div class="form-group"><label>Assigné</label><input type="text" id="dev_edit_assigned" value="${escapeHtml(d.assignedTo || "")}" ${writable ? "" : "disabled"}></div>
      <div class="form-group"><label>Statut</label><select id="dev_edit_status" ${writable ? "" : "disabled"}>${optStatus}</select></div>
      <div class="form-group"><label>Date résolution</label><input type="date" id="dev_edit_resolved" value="${d.resolvedDate || ""}" ${writable ? "" : "disabled"}></div>
      ${
        sponsorRequired
          ? `<div class="form-group" style="grid-column:1/-1">
              <label>Signalement au sponsor <span class="required-mark">*</span> ${deadline ? `<span class="field-hint">(échéance : ${deadline})</span>` : ""} ${d.sponsorReportOverdue ? `<span class="badge badge-danger">⚠️ En retard</span>` : ""}</label>
              <label style="display:inline-flex;align-items:center;gap:6px;font-weight:400">
                <input type="checkbox" id="dev_edit_reported" ${d.reportedToSponsor ? "checked" : ""} ${canSponsor ? "" : "disabled"}> Signalé au sponsor
              </label>
              <input type="date" id="dev_edit_sponsor_date" value="${d.sponsorReportDate || ""}" ${canSponsor ? "" : "disabled"}>
            </div>`
          : ""
      }
    </div>
    <div class="section" style="margin-top:18px"><div class="section-header"><h4>🧭 Plan CAPA</h4></div>
      <div class="section-body">
        <div class="form-grid">
          <div class="form-group" style="grid-column:1/-1"><label>Analyse des causes racines</label><textarea id="capa_rca" rows="3" ${canSponsor ? "" : "disabled"}>${escapeHtml(d.description || "")}</textarea></div>
          <div class="form-group" style="grid-column:1/-1"><label>Correction</label><textarea id="capa_correction" rows="3" ${canSponsor ? "" : "disabled"}>${escapeHtml(d.action || "")}</textarea></div>
        </div>
        <h5 style="margin:14px 0 4px">Actions correctives</h5>
        ${renderActionList(id, "corrective", actions, canSponsor)}
        <h5 style="margin:14px 0 4px">Actions préventives</h5>
        ${renderActionList(id, "preventive", actions, canSponsor)}
      </div>
    </div>`,
    writable
      ? `<button class="btn btn-secondary" id="devClose">Fermer</button><button class="btn btn-primary" id="devSaveEdit">Enregistrer</button>`
      : `<button class="btn btn-secondary" id="devClose">Fermer</button>`,
    true
  );
  $("#devClose")!.addEventListener("click", closeModal);
  $("#devSaveEdit")?.addEventListener("click", async () => {
    const patch: Record<string, unknown> = {
      assignedTo: ($("#dev_edit_assigned") as HTMLInputElement).value,
      status: ($("#dev_edit_status") as HTMLSelectElement).value,
      resolvedDate: ($("#dev_edit_resolved") as HTMLInputElement).value || undefined,
    };
    if (canSponsor && sponsorRequired) {
      patch.reportedToSponsor = ($("#dev_edit_reported") as HTMLInputElement).checked;
      patch.sponsorReportDate = ($("#dev_edit_sponsor_date") as HTMLInputElement).value || undefined;
    }
    try {
      await deviationsApi.update(id, patch);
      if (canSponsor) {
        await deviationsApi.updateCapa(id, {
          description: ($("#capa_rca") as HTMLTextAreaElement).value || undefined,
          action: ($("#capa_correction") as HTMLTextAreaElement).value || undefined,
        });
      }
    } catch (err) {
      notify("error", err instanceof Error ? err.message : "Erreur de mise à jour");
      return;
    }
    closeModal();
    void renderDeviationsView(root);
  });

  if (!canSponsor) return;

  $$<HTMLElement>("[data-capa-add-row]").forEach((container) => {
    const kind = container.dataset.capaAddRow as "corrective" | "preventive";

    const showButton = () => {
      container.innerHTML = renderAddButton(kind);
      container.querySelector<HTMLButtonElement>("[data-capa-add]")!.addEventListener("click", showForm);
    };

    const showForm = () => {
      container.innerHTML = renderNewActionForm(kind);
      container.querySelector<HTMLTextAreaElement>("[data-capa-new-desc]")!.focus();
      container.querySelector<HTMLButtonElement>("[data-capa-new-cancel]")!.addEventListener("click", showButton);
      container.querySelector<HTMLButtonElement>("[data-capa-new-save]")!.addEventListener("click", async () => {
        const descEl = container.querySelector<HTMLTextAreaElement>("[data-capa-new-desc]")!;
        const dueEl = container.querySelector<HTMLInputElement>("[data-capa-new-due]")!;
        const description = descEl.value.trim();
        if (!description) {
          descEl.classList.add("field-error");
          descEl.focus();
          return;
        }
        try {
          await deviationActionsApi.create(id, { kind, description, dueDate: dueEl.value || undefined });
        } catch (err) {
          notify("error", err instanceof Error ? err.message : "Erreur lors de l'ajout");
          return;
        }
        await openDeviationModal(id, root);
      });
    };

    container.querySelector<HTMLButtonElement>("[data-capa-add]")!.addEventListener("click", showForm);
  });
  $$("[data-capa-desc]").forEach((el) => {
    el.addEventListener("change", async (e) => {
      const actionId = (e.target as HTMLElement).dataset.capaDesc!;
      await deviationActionsApi.update(id, actionId, { description: (e.target as HTMLTextAreaElement).value });
    });
  });
  $$("[data-capa-due]").forEach((el) => {
    el.addEventListener("change", async (e) => {
      const actionId = (e.target as HTMLElement).dataset.capaDue!;
      await deviationActionsApi.update(id, actionId, { dueDate: (e.target as HTMLInputElement).value || null });
    });
  });
  $$("[data-capa-delete]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      if (!confirm("Supprimer cette action ?")) return;
      await deviationActionsApi.remove(id, btn.dataset.capaDelete!);
      await openDeviationModal(id, root);
    });
  });
  $$("[data-capa-remove-file]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      await deviationActionsApi.removeDeliverable(id, btn.dataset.capaRemoveFile!);
      await openDeviationModal(id, root);
    });
  });
  $$<HTMLInputElement>("[data-capa-file]").forEach((input) => {
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) return;
      const actionId = input.dataset.capaFile!;
      try {
        await deviationActionsApi.uploadDeliverable(id, actionId, file);
      } catch (err) {
        notify("error", err instanceof Error ? err.message : "Erreur d'envoi du fichier");
      }
      await openDeviationModal(id, root);
    });
  });
}
